// Free OpenStreetMap-based distance calculation (no API key required)

// Cache for geocoded postcodes to avoid repeated API calls
const geocodeCache = new Map();

// Clear cache function (call this to force fresh lookups)
export const clearGeocodeCache = () => {
  geocodeCache.clear();
  console.log('🗑️  Geocode cache cleared');
};

// Rate limiting: track last request time
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1500; // Increased to 1.5 seconds between requests

// Wait function for rate limiting
const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// UK bounds for validation
const UK_BOUNDS = {
  minLat: 49.5,
  maxLat: 61.0,
  minLon: -8.5,
  maxLon: 2.0
};

// Validate if coordinates are within UK
const isInUK = (lat, lon) => {
  return lat >= UK_BOUNDS.minLat && 
         lat <= UK_BOUNDS.maxLat && 
         lon >= UK_BOUNDS.minLon && 
         lon <= UK_BOUNDS.maxLon;
};

// Try UK-specific postcode API first (more accurate for UK postcodes)
const geocodeUKPostcode = async (postcode, retryCount = 0) => {
  try {
    // Try both with and without spaces - UK postcodes.io is picky
    const formatted1 = postcode.trim().toUpperCase().replace(/\s+/g, ''); // No spaces: LE181YQ
    const formatted2 = postcode.trim().toUpperCase(); // Original with spaces: LE18 1YQ
    
    // Try without spaces first
    for (const formatted of [formatted1, formatted2]) {
      const url = `https://api.postcodes.io/postcodes/${encodeURIComponent(formatted)}`;
      
      console.log(`🇬🇧 Trying UK Postcodes.io API: ${formatted} (attempt ${retryCount + 1})`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      try {
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        
        if (response.ok) {
          const data = await response.json();
          if (data.status === 200 && data.result) {
            const coords = {
              lat: data.result.latitude,
              lon: data.result.longitude
            };
            console.log(`✅ UK API success: ${formatted} = ${coords.lat}, ${coords.lon}`);
            return coords;
          } else if (data.status === 404) {
            console.log(`⚠️  Postcode not found: ${formatted}, trying next format...`);
            continue; // Try next format
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          console.error(`❌ UK API timeout for ${formatted}`);
          continue;
        }
        console.log(`⚠️  UK API error for ${formatted}: ${err.message}`);
      }
    }
    
    // If both formats failed and we have retries left
    if (retryCount < 2) {
      const delay = Math.pow(2, retryCount) * 1000;
      console.log(`⏳ Both formats failed, retrying in ${delay}ms...`);
      await wait(delay);
      return await geocodeUKPostcode(postcode, retryCount + 1);
    }
    
    console.log(`⚠️  UK API exhausted for ${postcode}, trying Nominatim...`);
    return null;
  } catch (error) {
    console.error(`❌ UK API error: ${error.message}`);
    return null;
  }
};

// Geocode a postcode/address to coordinates using Nominatim (free OSM service)
// Geocode a postcode/address to coordinates using multiple APIs
const geocodePostcode = async (postcode) => {
  // Check cache first
  const cacheKey = postcode.trim().toUpperCase().replace(/\s+/g, '');
  if (geocodeCache.has(cacheKey)) {
    console.log(`📦 Using cached coordinates for ${postcode}`);
    return geocodeCache.get(cacheKey);
  }

  // Try UK-specific API first (more accurate, faster, no rate limits)
  const ukCoords = await geocodeUKPostcode(postcode);
  if (ukCoords) {
    geocodeCache.set(cacheKey, ukCoords);
    return ukCoords;
  }

  // Fallback to Nominatim with rate limiting
  try {
    // Rate limiting: wait if needed
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
      const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
      console.log(`⏳ Rate limiting: waiting ${waitTime}ms before geocoding ${postcode}`);
      await wait(waitTime);
    }
    lastRequestTime = Date.now();

    // Format postcode properly - try multiple query formats
    const formatted = postcode.trim().toUpperCase();
    
    // Try multiple search strategies
    const searchStrategies = [
      `${formatted}, UK`,
      `${formatted} United Kingdom`,
      `postcode ${formatted} UK`,
    ];
    
    for (const searchQuery of searchStrategies) {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(searchQuery)}&countrycodes=gb&format=json&limit=1`;
      
      console.log(`🔍 Geocoding with Nominatim: "${searchQuery}"`);
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 seconds
      
      try {
        const response = await fetch(url, {
          headers: {
            'User-Agent': 'TransportManagementSystem/1.0'
          },
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          console.error(`❌ Nominatim API error: ${response.status} for "${searchQuery}"`);
          continue; // Try next strategy
        }
        
        const data = await response.json();
        
        if (data && data.length > 0) {
          const coords = {
            lat: parseFloat(data[0].lat),
            lon: parseFloat(data[0].lon)
          };
          
          // CRITICAL: Validate coordinates are in UK
          if (!isInUK(coords.lat, coords.lon)) {
            console.error(`❌ Coordinates outside UK: ${coords.lat}, ${coords.lon}`);
            console.error(`   Location found: ${data[0].display_name}`);
            continue; // Try next strategy
          }
          
          // Cache the result
          geocodeCache.set(cacheKey, coords);
          console.log(`✅ Nominatim success: ${postcode} = ${coords.lat}, ${coords.lon}`);
          console.log(`   Location: ${data[0].display_name}`);
          
          return coords;
        }
        
        console.log(`⚠️  No results for "${searchQuery}", trying next strategy...`);
      } catch (err) {
        if (err.name === 'AbortError') {
          console.error(`❌ Nominatim timeout for "${searchQuery}"`);
        } else {
          console.error(`❌ Nominatim error: ${err.message}`);
        }
        continue; // Try next strategy
      }
      
      // Wait between strategies to respect rate limits
      await wait(1500);
    }
    
    console.error(`❌ All geocoding strategies failed for: ${postcode}`);
    return null;
  } catch (error) {
    console.error(`❌ Geocoding completely failed for ${postcode}:`, error.message);
    return null;
  }
};

// Calculate route distance using OSRM (free, no API key required)
const getRouteDistance = async (coordinates, retryCount = 0) => {
  try {
    const coordStr = coordinates.map(c => `${c.lon},${c.lat}`).join(';');
    const url = `https://router.project-osrm.org/route/v1/driving/${coordStr}?overview=false`;
    
    console.log(`🗺️  Calculating driving distance with OSRM...`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      console.error(`❌ OSRM API error: ${response.status}`);
      
      // Retry once if server error
      if (response.status >= 500 && retryCount < 1) {
        console.log(`🔄 Retrying OSRM...`);
        await wait(2000);
        return await getRouteDistance(coordinates, retryCount + 1);
      }
      
      // Fallback to straight-line
      console.log(`⚠️  Falling back to straight-line estimate...`);
      return calculateStraightLineDistance(coordinates[0], coordinates[1]);
    }
    
    const data = await response.json();
    
    if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
      const meters = data.routes[0].distance;
      const miles = meters * 0.000621371;
      console.log(`✅ OSRM: ${miles.toFixed(1)} miles`);
      return miles;
    }
    
    console.warn(`⚠️  No route found, using straight-line estimate`);
    return calculateStraightLineDistance(coordinates[0], coordinates[1]);
  } catch (error) {
    console.error('❌ Routing error:', error.message);
    
    // Retry once on error
    if (retryCount < 1) {
      console.log(`🔄 Retrying...`);
      await wait(2000);
      return await getRouteDistance(coordinates, retryCount + 1);
    }
    
    // Final fallback
    console.log(`⚠️  Using straight-line estimate as fallback`);
    return calculateStraightLineDistance(coordinates[0], coordinates[1]);
  }
};

// Calculate straight-line distance as fallback (Haversine formula)
const calculateStraightLineDistance = (coord1, coord2) => {
  const R = 3958.8; // Earth's radius in miles
  const lat1 = coord1.lat * Math.PI / 180;
  const lat2 = coord2.lat * Math.PI / 180;
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLon = (coord2.lon - coord1.lon) * Math.PI / 180;
  
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  const roadEstimate = distance * 1.3; // Multiply by 1.3 for road distance estimate
  console.log(`📏 Straight-line fallback: ${roadEstimate.toFixed(1)} miles (${distance.toFixed(1)} direct × 1.3)`);
  return roadEstimate;
};

// Main distance calculation with validation
export const calculateDistance = async (origin, destination) => {
  try {
    console.log(`\n🔍 Calculating distance: ${origin} → ${destination}`);
    
    // Geocode both postcodes
    const coords1 = await geocodePostcode(origin);
    const coords2 = await geocodePostcode(destination);
    
    if (!coords1 || !coords2) {
      console.error(`❌ Failed to geocode: ${!coords1 ? origin : destination}`);
      return 0;
    }
    
    console.log(`   Coords: (${coords1.lat}, ${coords1.lon}) → (${coords2.lat}, ${coords2.lon})`);
    
    // Get driving distance
    const distance = await getRouteDistance([coords1, coords2]);
    
    // VALIDATION: Check if distance makes sense for UK
    if (distance > 700) {
      console.error(`❌ VALIDATION FAILED: Distance ${distance.toFixed(1)} miles exceeds max UK distance (~700 miles)`);
      console.error(`   This suggests wrong coordinates were found`);
      console.error(`   Postcodes: ${origin} → ${destination}`);
      return 0; // Return 0 to indicate error
    }
    
    if (distance < 0.1 && origin.trim().toUpperCase() !== destination.trim().toUpperCase()) {
      console.warn(`⚠️  VALIDATION: Distance too small (${distance.toFixed(1)} miles) for different postcodes`);
      console.warn(`   Postcodes: ${origin} → ${destination}`);
    }
    
    console.log(`✅ Valid distance: ${distance.toFixed(1)} miles\n`);
    return distance;
  } catch (error) {
    console.error(`❌ Error calculating distance from ${origin} to ${destination}:`, error);
    return 0;
  }
};

// Calculate total route mileage for a vehicle's deliveries
export const calculateRouteMileage = async (basePostcode, postcodes) => {
  if (!basePostcode || !postcodes || postcodes.length === 0) {
    return 0;
  }

  // Filter out empty postcodes
  const validPostcodes = postcodes.filter(pc => pc && pc.trim());
  
  if (validPostcodes.length === 0) {
    return 0;
  }

  try {
    // Geocode all locations
    const baseCoords = await geocodePostcode(basePostcode);
    if (!baseCoords) {
      console.error('Could not geocode base postcode:', basePostcode);
      return 0;
    }

    const stopCoords = [];
    for (const postcode of validPostcodes) {
      const coords = await geocodePostcode(postcode);
      if (coords) {
        stopCoords.push(coords);
      }
    }

    if (stopCoords.length === 0) {
      return 0;
    }

    // Build route: base -> stop1 -> stop2 -> ... -> base
    const routeCoords = [baseCoords, ...stopCoords, baseCoords];
    
    // Get total route distance
    const totalMiles = await getRouteDistance(routeCoords);
    
    return Math.round(totalMiles * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    console.error('Error calculating route mileage:', error);
    return 0;
  }
};

// Calculate mileage for a single vehicle based on its delivery entries
export const calculateVehicleMileage = async (basePostcode, entries) => {
  if (!entries || entries.length === 0) {
    return 0;
  }

  // Extract postcodes from entries
  const postcodes = entries
    .map(entry => entry.postcode)
    .filter(pc => pc && pc.trim());

  if (postcodes.length === 0) {
    return 0;
  }

  return await calculateRouteMileage(basePostcode, postcodes);
};
